/**
 * Date/Time Utility Functions
 *
 * TIMEZONE STRATEGY:
 * - All times stored in the database are in UTC.
 * - Frontend receives UTC times and converts them to local for display.
 * - Frontend converts local times to UTC before sending to backend.
 * - JavaScript's Date object handles timezone conversion automatically
 *   when using getHours() vs getUTCHours(), etc.
 */

/**
 * Parse a PHP/MySQL datetime string or Unix timestamp from the server.
 * Server data is always in UTC.
 *
 * @param dateValue - Unix timestamp (seconds) or ISO/MySQL datetime string (UTC)
 * @returns Date object in local timezone
 */
export function phpDate(dateValue: number | string): Date {
	let date = new Date();

	if (typeof dateValue === "number") {
		// Unix timestamp (seconds) - convert to milliseconds
		date = new Date(dateValue * 1000);
	} else {
		// String datetime - assume UTC if no timezone specified
		const dateStr = String(dateValue).trim();
		date = new Date(dateStr);
	}

	return new Date(date.getTime() - offsetMs());
}

/**
 * Check if a date is valid
 */
export function isValidDate(date: unknown): date is Date {
	if (!(date instanceof Date)) {
		return false;
	}
	return !isNaN(date.getTime());
}

/**
 * Convert a local hour to UTC hour.
 * Note: This only works correctly for the current day.
 *
 * @param localHour - Hour in local timezone (0-23)
 * @returns Hour in UTC (0-23)
 */
export function utcHour(localHour: number): number {
	const date = new Date();
	date.setHours(localHour, 0, 0, 0);
	return date.getUTCHours();
}

/**
 * Convert a UTC hour to local hour.
 *
 * @param utcHourValue - Hour in UTC (0-23)
 * @returns Hour in local timezone (0-23)
 */
export function localHour(utcHourValue: number): number {
	const date = new Date();
	date.setUTCHours(utcHourValue, 0, 0, 0);
	return date.getHours();
}

/**
 * Convert a local minute to UTC minute.
 *
 * @param localMinute - Minute in local timezone (0-59)
 * @returns Minute in UTC (0-59)
 */
export function utcMinute(localMinute: number): number {
	const date = new Date();
	date.setMinutes(localMinute);
	return date.getUTCMinutes();
}

/**
 * Convert a UTC minute to local minute.
 *
 * @param utcMinuteValue - Minute in UTC (0-59)
 * @returns Minute in local timezone (0-59)
 */
export function localMinute(utcMinuteValue: number): number {
	const date = new Date();
	date.setUTCMinutes(utcMinuteValue);
	return date.getMinutes();
}

/**
 * Convert local time (hour:minute) to UTC time.
 * This properly handles day boundary crossings.
 *
 * @param hour - Local hour (0-23)
 * @param minute - Local minute (0-59)
 * @returns Object with UTC hour and minute
 */
export function localTimeToUtc(hour: number, minute: number): { hour: number; minute: number } {
	const date = new Date();
	date.setHours(hour, minute, 0, 0);
	return {
		hour: date.getUTCHours(),
		minute: date.getUTCMinutes(),
	};
}

/**
 * Convert UTC time (hour:minute) to local time.
 * This properly handles day boundary crossings.
 *
 * @param hour - UTC hour (0-23)
 * @param minute - UTC minute (0-59)
 * @returns Object with local hour and minute
 */
export function utcTimeToLocal(hour: number, minute: number): { hour: number; minute: number } {
	const date = new Date();
	date.setUTCHours(hour, minute, 0, 0);
	return {
		hour: date.getHours(),
		minute: date.getMinutes(),
	};
}

/**
 * Convert a local weekday to UTC weekday.
 * This accounts for timezone differences that might shift the day.
 *
 * @param localWeekday - Local weekday (0=Sunday, 6=Saturday)
 * @param localHour - Optional local hour for more accurate conversion
 * @returns UTC weekday (0=Sunday, 6=Saturday)
 */
export function utcWeekday(localWeekday: number, localHour = 12): number {
	const now = new Date();
	// Get to the specified weekday
	const currentDay = now.getDay();
	const daysToAdd = (localWeekday - currentDay + 7) % 7;

	const targetDate = new Date(now);
	targetDate.setDate(now.getDate() + daysToAdd);
	targetDate.setHours(localHour, 0, 0, 0);

	return targetDate.getUTCDay();
}

/**
 * Convert a UTC weekday to local weekday.
 *
 * @param utcWeekdayValue - UTC weekday (0=Sunday, 6=Saturday)
 * @param utcHour - Optional UTC hour for more accurate conversion
 * @returns Local weekday (0=Sunday, 6=Saturday)
 */
export function localWeekday(utcWeekdayValue: number, utcHour = 12): number {
	const now = new Date();
	const currentUtcDay = now.getUTCDay();
	const daysToAdd = (utcWeekdayValue - currentUtcDay + 7) % 7;

	const targetDate = new Date(now);
	targetDate.setUTCDate(now.getUTCDate() + daysToAdd);
	targetDate.setUTCHours(utcHour, 0, 0, 0);

	return targetDate.getDay();
}

export const MS_IN_A_DAY = 24 * 60 * 60 * 1000;

/**
 * Get an array of Date objects starting from a given date.
 *
 * @param numberOfDates - How many dates to return
 * @param startDate - Starting date (defaults to now)
 * @returns Array of Date objects
 */
export function getDates(numberOfDates: number, startDate = new Date()): Date[] {
	const dates: Date[] = [];

	for (let i = 0; i < numberOfDates; i++) {
		const time = startDate.getTime() + i * MS_IN_A_DAY;
		const date = new Date(time);
		dates.push(date);
	}

	return dates;
}

/**
 * Get the current timezone offset in milliseconds.
 * Positive offset = behind UTC, Negative offset = ahead of UTC.
 *
 * @returns Offset in milliseconds
 */
export function offsetMs(): number {
	return new Date().getTimezoneOffset() * 60 * 1000;
}

/**
 * Create a UTC Date from a date that was intended to be interpreted as local.
 */
export function utcDate(localDate: Date): Date {
	return new Date(
		Date.UTC(
			localDate.getFullYear(),
			localDate.getMonth(),
			localDate.getDate(),
			localDate.getHours(),
			localDate.getMinutes(),
			localDate.getSeconds(),
			localDate.getMilliseconds()
		)
	);
}

/**
 * Interpret a UTC-created Date as if it were local time.
 */
export function localDate(utcDateVal: Date): Date {
	return new Date(
		utcDateVal.getUTCFullYear(),
		utcDateVal.getUTCMonth(),
		utcDateVal.getUTCDate(),
		utcDateVal.getUTCHours(),
		utcDateVal.getUTCMinutes(),
		utcDateVal.getUTCSeconds(),
		utcDateVal.getUTCMilliseconds()
	);
}

/**
 * Get the next scheduled run time based on weekdays and times.
 * NOTE: This function expects weekdays and times in LOCAL timezone
 * (as converted by the useAutoPostings hook).
 *
 * @param weekdays - Array of local weekdays (0-6, 0=Sunday)
 * @param times - Array of time objects with hour and minute in local time
 * @returns Next run Date or null if none found
 */
export function getNextScheduledRun(
	weekdays: number[],
	times: Array<{ hour: number; minute: number }>
): Date | null {
	if (!weekdays.length || !times.length) {
		return null;
	}

	const now = new Date();
	const futureRuns: Date[] = [];

	// Check next 8 days to ensure we cover all weekdays
	for (let dayOffset = 0; dayOffset < 8; dayOffset++) {
		const checkDate = new Date(now.getTime() + dayOffset * MS_IN_A_DAY);
		// Use local weekday since data is already converted to local by the hook
		const localWeekdayValue = checkDate.getDay();

		if (!weekdays.includes(localWeekdayValue)) {
			continue;
		}

		for (const time of times) {
			const runDate = new Date(checkDate);
			// Use local time since data is already converted to local by the hook
			runDate.setHours(time.hour, time.minute, 0, 0);

			// Only include future times
			if (runDate > now) {
				futureRuns.push(runDate);
			}
		}
	}

	if (!futureRuns.length) {
		return null;
	}

	// Return the closest future run
	futureRuns.sort((a, b) => a.getTime() - b.getTime());
	return futureRuns[0];
}

